import { modelOptions, mongoose, post, prop, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../../zod-decorator/index.js";
import { translationParamsZodValidator } from "../../commonZodValidators/translationParams.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type { WarningFixEntryFieldsWithDefaults, WarningFixEntrySchemaTypes } from "../../types/warningFixEntry.d.js";
import { trackMixed } from "../../utils/mixedSchemaSavingUtils.js";
import type { DtoValidatorTypes } from "../../validatableClass.js";
import { declaredKeyValidator } from "./declaredKeyValidator.js";

const SCHEMA_KEY = "WarningFixEntry" as const;

/**
 * One entry of a warning's `warningFixes` list: a "suggested fix" identified by
 * `fixKey` with a catalog string under `<warningType>.fixes.<fixKey>`.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<WarningFixEntry>>(...addSchemaKeyHook(SCHEMA_KEY))
export class WarningFixEntry extends SchemaBase<WarningFixEntry, WarningFixEntryFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * Catalog identifier for this fix. Must be declared in the owning warning type's
     * `possibleFixKeys`; standalone entries (no owner document) skip that check, as
     * does an unknown warningType — its own validator is the sole reporter.
     */
    @validate({ zodValidator: z.string().min(1), responseOnly: true })
    @prop({ type: () => String, required: true, validate: declaredKeyValidator("possibleFixKeys") })
    public fixKey!: string;

    /**
     * Interpolation values for the fix's translation template, subject to the same
     * i18next reserved-key rules as `warningParams`.
     */
    @validate({ zodValidator: translationParamsZodValidator().optional(), responseOnly: true })
    @trackMixed
    @prop({
        type: () => mongoose.Schema.Types.Mixed,
        required: false,
        allowMixed: Severity.ALLOW,
        validate: mongooseValidatorFromZod(translationParamsZodValidator()),
    })
    public fixParams?: Record<string, string | number>;

    /**
     * Default-locale display text persisted at save time so the UI has a readable
     * fallback if the catalog key is later removed or renamed.
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public defaultDisplayText?: string;

    static getters = {} as const satisfies Record<string, (self: WarningFixEntry) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<WarningFixEntrySchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<WarningFixEntry, WarningFixEntryFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

WarningFixEntry.dtoValidators = getZodSchemaFromClass(
    WarningFixEntry,
) as unknown as DtoValidatorTypes<WarningFixEntrySchemaTypes>;
